import React, { useState, useEffect, useMemo, useCallback } from 'react'; // eslint-disable-line no-unused-vars
import { Clock, Zap, ZapOff, ArrowUp, ArrowDown, AlertTriangle, Briefcase, CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { db } from '../../firebase.js';
import { getFunctions, httpsCallable } from 'firebase/functions'; // Importamos estas para Cloud Functions
import '../../styles/DetailsModal.css';
import { doc, collection, serverTimestamp, addDoc, query, where, getDocs, runTransaction } from 'firebase/firestore';
import '../../styles/MisAcuerdos.css';
import '../../styles/Reportes.css';
import Pagination from '../../components/common/Pagination.js';
import StatusBadge from '../../components/common/StatusBadge.js'; // Importamos el nuevo componente unificado
import AgreementProgressBarVisual from '../../utils/AgreementProgressBar.js'; // Importar solo el componente visual
import AgreementDetailsModal from '../../components/modals/AgreementDetailsModal.js';
import { getAgreementProgressDetails, distributeCommissionsToAgreements } from '../../utils/agreementUtils.js'; // Importar ambas funciones
import usePlatformConfig from '../../hooks/usePlatformConfig.js';

const AGREEMENT_STATUS = {
    APROBADO: 'aprobado',
    COMPLETADO: 'completado',
    PENDIENTE: 'pendiente',
    RECHAZADO: 'rechazado',
    CANCELADO: 'cancelado',
};

const REQUEST_STATUS = {
    PENDIENTE: 'pendiente',
};

const CLOUD_FUNCTIONS = {
    MARK_COMPLETED: 'markAgreementAsCompleted',
    UPLOAD_FILE: 'uploadFile',
};

const TRANSACTION_TYPES = {
    FEE_ACELERADOR_PENDIENTE: 'fee_acelerador_pendiente',
};

const TABS = ['Todos', 'Activos', 'Completados', 'Pendientes', 'Rechazados', 'Cancelados'];

const escapeHtml = (value = '') => {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value).replace(/[&<>"']/g, (char) => map[char]);
};

const renderWalletsHtml = (wallets) => {
    if (!wallets || wallets.length === 0) {
        return '<p>Contacta a soporte para obtener la billetera donde realizar el pago.</p>';
    }
    return wallets.map(wallet => `
        <p><strong>${escapeHtml(wallet.label)}:</strong></p>
        <p class="wallet-address">${escapeHtml(wallet.address)}</p>
    `).join('');
};

const getDateFromTimestamp = (ts) => {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
    if (typeof ts === 'number') return new Date(ts);
    if (typeof ts === 'string') {
        const parsed = Date.parse(ts);
        return Number.isNaN(parsed) ? null : new Date(parsed);
    }
    return null;
};

// --- NUEVO: Componente para las tarjetas de resumen ---
const ACCELERATOR_WARNING_DAYS = 2;

const SummaryCard = ({ title, value, icon }) => (
    <div className="summary-card-acuerdos">
        <div className="summary-card-icon">{icon}</div>
        <div className="summary-card-info">
            <p className="summary-card-title">{title}</p>
            <p className="summary-card-value">{value}</p>
        </div>
    </div>
);


const MisAcuerdos = ({ agreements, user, profileData, financialSummary, transactions }) => {
    const [activeTab, setActiveTab] = useState('Todos');
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingReactivation, setLoadingReactivation] = useState(null);

    // Cloud Function callable para marcar un acuerdo como completado
    const [showDetailsModal, setShowDetailsModal] = useState(false); // Estado para controlar la visibilidad del modal
    const [selectedAgreement, setSelectedAgreement] = useState(null); // Estado para el acuerdo seleccionado

    // --- NUEVO: Estado para la ordenación de la tabla ---
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

    const [pendingRequests, setPendingRequests] = useState(new Set());
    const [itemsPerPage, setItemsPerPage] = useState(15);

    const markAgreementAsCompleted = httpsCallable(getFunctions(), 'api');

    const { config: platformConfig } = usePlatformConfig();
    const depositWallets = useMemo(() => {
        if (!platformConfig || !Array.isArray(platformConfig.depositWallets)) {
            return [];
        }
        return platformConfig.depositWallets
            .map((wallet, index) => ({
                id: wallet?.id || `wallet-${index}`,
                label: (wallet?.label || '').trim(),
                address: (wallet?.address || '').trim(),
            }))
            .filter(wallet => wallet.label && wallet.address);
    }, [platformConfig]);

    // --- NUEVO: Cálculo de los datos para las tarjetas de resumen ---
    // --- CORRECCIÓN: Lógica de distribución de comisiones ---
    const commissionAllocation = useMemo(() => {
        const totalCommissions = financialSummary?.totalCommissions || 0;
        if (!agreements || agreements.length === 0) return new Map();
        return distributeCommissionsToAgreements(agreements, totalCommissions, transactions);
    }, [agreements, financialSummary, transactions]);

    // --- MEJORA: Pre-calcular los detalles de progreso para evitar recálculos ---
    const processedAgreements = useMemo(() => {
        if (!agreements || !transactions) return [];
        // Para cada acuerdo, calculamos sus detalles de progreso UNA SOLA VEZ.
        return agreements.map(agreement => {
            const allocatedCommission = commissionAllocation.get(agreement.id) || 0;
            return {
                ...agreement,
                progressDetails: getAgreementProgressDetails(agreement, allocatedCommission, transactions)
            };
        });
    }, [agreements, transactions, commissionAllocation]);

    const summaryData = useMemo(() => {
        if (!processedAgreements) return { total: 0, activos: 0, completados: 0, capitalInvertido: 0 };
        
        const activos = processedAgreements.filter(a => a.status === 'aprobado' && !a.progressDetails.isComplete).length;
        const completados = processedAgreements.filter(a => a.status === 'completado' || a.progressDetails.isComplete).length;
        const capitalInvertido = processedAgreements.filter(a => a.status === 'aprobado').reduce((sum, a) => sum + (a.netInvestmentAmount || a.amount || 0), 0);

        return {
            total: processedAgreements.length,
            activos,
            completados,
            capitalInvertido
        };
    }, [processedAgreements]);

    const acceleratorsAboutToExpire = useMemo(() => {
        if (!agreements || agreements.length === 0) return [];
        const now = new Date();
        const thresholdMs = ACCELERATOR_WARNING_DAYS * 24 * 60 * 60 * 1000;

        return agreements.reduce((alerts, agreement) => {
            if (!agreement || agreement.status !== 'aprobado' || agreement.acceleratorActive !== true) {
                return alerts;
            }

            const endDate = getDateFromTimestamp(agreement.acceleratorEndDate);
            if (!endDate) return alerts;

            const diffMs = endDate.getTime() - now.getTime();
            if (diffMs <= 0 || diffMs > thresholdMs) {
                return alerts;
            }

            const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
            alerts.push({
                agreement,
                agreementId: agreement.id,
                shortId: agreement.id?.substring(0, 6).toUpperCase(),
                endDate,
                daysLeft
            });
            return alerts;
        }, []).sort((a, b) => a.endDate - b.endDate);
    }, [agreements]);

    // Definir la lógica de autocompletado
    const handleAutoCompleteAgreement = useCallback(async (agreement) => {
        if (agreement.status === 'completado') return;
        try {
            await markAgreementAsCompleted({ __fn: CLOUD_FUNCTIONS.MARK_COMPLETED, agreementId: agreement.id });
        } catch (error) {
            console.error(`Error al autocompletar acuerdo ${agreement.id}:`, error);
        }
    }, [markAgreementAsCompleted]);

    useEffect(() => {
        const fetchPendingRequests = async () => {
            if (!user) return;
    
            const q1 = query(collection(db, "accelerator_requests"), where("userId", "==", user.uid), where("status", "==", REQUEST_STATUS.PENDIENTE));
            const q2 = query(collection(db, "accelerator_reactivation_requests"), where("userId", "==", user.uid), where("status", "==", REQUEST_STATUS.PENDIENTE));
    
            const [requestsSnap, reactivationRequestsSnap] = await Promise.all([getDocs(q1), getDocs(q2)]);
    
            const pendingAgreementIds = new Set();
            requestsSnap.forEach(doc => pendingAgreementIds.add(doc.data().agreementId));
            reactivationRequestsSnap.forEach(doc => pendingAgreementIds.add(doc.data().agreementId));
    
            setPendingRequests(pendingAgreementIds);
        };
    
        fetchPendingRequests();
    }, [user]);

    const handleReactivateAccelerator = async (agreement) => {
        setLoadingReactivation(agreement.id);
        const feeRate = platformConfig?.reactivationFeeRate || 0.05;
        const feeAmount = (agreement.netInvestmentAmount || agreement.amount) * feeRate;
        const userHasSufficientBalance = (profileData?.withdrawableBalance || 0) >= feeAmount;

        if (userHasSufficientBalance) { 
            const confirmation = await Swal.fire({
                title: 'Reactivar Acelerador',
                html: `
                    <p>Estás a punto de reactivar el acelerador para tu acuerdo <strong>#${agreement.id.substring(0, 6).toUpperCase()}</strong>.</p>
                    <p>Se aplicará una comisión del <strong>${feeRate * 100}%</strong> sobre el monto neto.</p>
                    <hr />
                    <p>Monto neto: <strong>${(agreement.netInvestmentAmount || agreement.amount).toFixed(2)}</strong></p>
                    <p>Comisión de Reactivación: <strong>${feeAmount.toFixed(2)}</strong></p>
                    <hr />
                    <p>Esta comisión se descontará de tu <strong>saldo disponible para retiro</strong>.</p>
                `,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Confirmar y Pagar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: 'var(--verde-botones)',
            });

            if (confirmation.isConfirmed) {
                try {
                    const userProfileRef = doc(db, `users/${user.uid}/profile/data`);
                    const reactivationRequestRef = doc(collection(db, 'accelerator_reactivation_requests'));

                    await runTransaction(db, async (transaction) => {
                        const profileDoc = await transaction.get(userProfileRef);
                        if (!profileDoc.exists) throw new Error("No se encontró tu perfil.");

                        const currentBalance = profileDoc.data().withdrawableBalance || 0;
                        if (currentBalance < feeAmount) {
                            throw new Error("Saldo insuficiente para la comisión.");
                        }

                        transaction.update(userProfileRef, { withdrawableBalance: currentBalance - feeAmount });
                        transaction.set(reactivationRequestRef, {
                            userId: user.uid,
                            userEmail: user.email,
                            agreementId: agreement.id,
                            feePaid: feeAmount, 
                            status: REQUEST_STATUS.PENDIENTE,
                            createdAt: serverTimestamp(),
                        });
                        const userFeeTxRef = doc(collection(db, `users/${user.uid}/balance_transactions`));
                        transaction.set(userFeeTxRef, {
                          type: TRANSACTION_TYPES.FEE_ACELERADOR_PENDIENTE,
                          amount: -feeAmount,
                          description: `Fee por reactivación de acelerador`,
                          relatedAgreementId: agreement.id,
                          sourceTransactionId: reactivationRequestRef.id,
                          createdAt: serverTimestamp(),
                        });
                    });

                    setPendingRequests(prev => new Set(prev).add(agreement.id));
                    Swal.fire('¡Solicitud Enviada!', 'El administrador ha sido notificado. Tu acelerador será reactivado pronto.', 'success');
                } catch (error) {
                    console.error("Error reactivating accelerator: ", error);
                    Swal.fire('Error', `Ocurrió un problema al enviar la solicitud: ${error.message}`, 'error');
                }
            }
        } else {
            const walletInstructionsHtml = renderWalletsHtml(depositWallets);
            const confirmation = await Swal.fire({
                title: 'Saldo Insuficiente',
                html: `
                    <div style="text-align: left; padding: 0 1rem;">
                        <p>Tu saldo disponible para retiro no es suficiente para la comisión de <strong>${feeAmount.toFixed(2)}</strong>.</p>
                        <p>Para continuar, realiza el pago a una de nuestras cuentas y sube el comprobante a continuación.</p>
                        <hr />
                        <div class="wallet-info">${walletInstructionsHtml}</div>
                        <hr />
                        <p><strong>Sube tu comprobante:</strong></p>
                        <input type="file" id="payment-proof-input" class="swal2-file" accept="image/*,application/pdf">
                    </div>
                `,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Crear Solicitud con Comprobante',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: 'var(--verde-botones)',
                preConfirm: () => {
                    const file = document.getElementById('payment-proof-input').files[0];
                    if (!file) {
                        Swal.showValidationMessage('Por favor, selecciona un archivo de comprobante.');
                        return false;
                    }
                    return file;
                }
            });

            if (confirmation.isConfirmed && confirmation.value) {
                const file = confirmation.value;
                setLoadingReactivation(agreement.id); 

                try {
                    const fileReader = new FileReader();
                    fileReader.readAsDataURL(file);
                    fileReader.onload = async () => {
                        const fileData = fileReader.result;
                        const uploadUrl = `https://us-central1-plataforma-invest-f1675.cloudfunctions.net/${CLOUD_FUNCTIONS.UPLOAD_FILE}`;
                        const uploadResponse = await fetch(uploadUrl, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                // El endpoint exige sesión (ver functions/index.js, uploadFile).
                                'Authorization': `Bearer ${await user.getIdToken()}`
                            },
                            body: JSON.stringify({ data: { file: fileData, path: `payment_proofs/${user.uid}/${agreement.id}_${Date.now()}` } })
                        });

                        if (!uploadResponse.ok) {
                            const errorBody = await uploadResponse.json();
                            throw new Error(errorBody.error.message || 'Error al subir el archivo.');
                        }

                        const uploadResult = await uploadResponse.json();
                        const downloadURL = uploadResult.data.downloadURL;

                        const newRequest = {
                            userId: user.uid,
                            userEmail: user.email,
                            agreementId: agreement.id,
                            feeAmount: feeAmount,
                            status: REQUEST_STATUS.PENDIENTE,
                            createdAt: serverTimestamp(),
                            agreementNetInvestment: (agreement.netInvestmentAmount || agreement.amount),
                            paymentProofUrl: downloadURL
                        };
                        await addDoc(collection(db, 'accelerator_reactivation_requests'), newRequest);
                        setPendingRequests(prev => new Set(prev).add(agreement.id));
                        Swal.fire('Solicitud Creada', 'Tu solicitud de reactivación ha sido enviada con el comprobante. Un administrador la revisará pronto.', 'success');
                        setLoadingReactivation(null);
                    };
                    fileReader.onerror = (error) => {
                        throw new Error('No se pudo leer el archivo.');
                    };

                } catch (error) {
                    console.error("Error creating manual reactivation request: ", error);
                    Swal.fire('Error', `Ocurrió un problema al crear la solicitud: ${error.message}`, 'error');
                    setLoadingReactivation(null);
                }
            }
        }

        setLoadingReactivation(null);
    };

    const filteredAgreements = useMemo(() => {
        return (processedAgreements || []).filter(a => {
            const { isComplete } = a.progressDetails; // Usamos el detalle pre-calculado

            switch (activeTab) {
                case 'Todos': return true;
                case 'Activos': return a.status === AGREEMENT_STATUS.APROBADO && !isComplete;
                case 'Completados': return a.status === AGREEMENT_STATUS.COMPLETADO || isComplete;
                case 'Pendientes': return a.status === AGREEMENT_STATUS.PENDIENTE;
                case 'Rechazados': return a.status === AGREEMENT_STATUS.RECHAZADO;
                case 'Cancelados': return a.status === AGREEMENT_STATUS.CANCELADO;
                default: return false;
            }
        });
    }, [processedAgreements, activeTab]);

    const getAcceleratorSortValue = useCallback((agreement) => {
        if (pendingRequests.has(agreement.id)) {
            return 2; // Pendiente
        }
        if (agreement.acceleratorActive) {
            return 1; // Activo
        }
        if (agreement.deactivationReason) {
            return 3; // Desactivado/Expirado
        }
        return 4; // No incluido
    }, [pendingRequests]);

    const sortedAgreements = useMemo(() => {
        let sortableItems = [...filteredAgreements];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                if (sortConfig.key === 'currentValue') {
                    valA = valA || 0;
                    valB = valB || 0;
                } else if (sortConfig.key === 'netInvestmentAmount') {
                    valA = valA || a.amount || 0;
                    valB = valB || b.amount || 0;
                } else if (valA?.toDate && valB?.toDate) { 
                    valA = valA.toDate();
                    valB = valB.toDate();
                } else if (sortConfig.key === 'acceleratorStatus') {
                    valA = getAcceleratorSortValue(a);
                    valB = getAcceleratorSortValue(b);
                } else if (sortConfig.key === 'progress') {
                    valA = a.progressDetails.progress; // Usamos el detalle pre-calculado
                    valB = b.progressDetails.progress; // Usamos el detalle pre-calculado
                }

                if (valA < valB) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredAgreements, sortConfig, getAcceleratorSortValue]); // 'commissionAllocation' y 'transactions' ya no son dependencias directas aquí

    const paginatedAgreements = sortedAgreements.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const totalPages = Math.ceil(sortedAgreements.length / itemsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <span className="sort-icon-placeholder"></span>;
        }
        if (sortConfig.direction === 'ascending') {
            return <ArrowUp size={14} className="sort-icon" />;
        }
        return <ArrowDown size={14} className="sort-icon" />;
    };

    const handleOpenDetailsModal = (agreement) => {
        setSelectedAgreement(agreement);
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedAgreement(null);
    };

    const AcceleratorInfo = ({ agreement, pendingRequests, transactions }) => {
        // Usamos el detalle pre-calculado que ya viene en el objeto 'agreement'
        const { isComplete } = agreement.progressDetails;

        const hasPendingRequest = pendingRequests.has(agreement.id);
    
        if (hasPendingRequest) {
            return <span className="accelerator-pending">Solicitud Pendiente</span>;
        }
    
        if (agreement.acceleratorActive) {
            const endDate = agreement.acceleratorEndDate?.toDate ? agreement.acceleratorEndDate.toDate() : null;
            if (!endDate) return <span className="accelerator-active">Sí (Procesando)</span>;
    
            const startDate = agreement.acceleratorStartDate?.toDate ? agreement.acceleratorStartDate.toDate() : null;
            const now = new Date();

            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);

            const totalDurationMs = endDate.getTime() - startDate.getTime();
            const elapsedMs = Math.max(0, now.getTime() - startDate.getTime());
            const percentageElapsed = totalDurationMs > 0 ? Math.min((elapsedMs / totalDurationMs) * 100, 100) : 0;
            const daysRemaining = Math.max(0, Math.ceil((endOfDay.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
            const dateOptions = { year: '2-digit', month: 'numeric', day: 'numeric' };
    
            return (
                <div className="accelerator-info">
                    <div className="accelerator-dates compact">
                        <span>{startDate.toLocaleDateString('es-ES', dateOptions)} - {endDate.toLocaleDateString('es-ES', dateOptions)}</span>
                    </div>
                    {totalDurationMs > 0 && (
                        <div className="progress-bar-wrapper small">
                            <div className="progress-bar" style={{ width: `${percentageElapsed}%` }}></div>
                        </div>
                    )}
                    <span className="accelerator-remaining"><Clock size={12} /> {daysRemaining} {daysRemaining === 1 ? 'día restante' : 'días restantes'}</span>
                </div>
            );
        }
    
        if (agreement.deactivationReason || (agreement.acceleratorEndDate && new Date() >= agreement.acceleratorEndDate.toDate())) {
            const reason = agreement.deactivationReason || 'Expirado';
            if (!isComplete) {
                return (
                    <div className="accelerator-status-container">
                        <span className="accelerator-deactivated" title={`Motivo: ${reason}`}><ZapOff size={16} /> Desactivado</span>
                        <button onClick={(e) => { e.stopPropagation(); handleReactivateAccelerator(agreement); }} className="reactivate-button small" disabled={loadingReactivation === agreement.id}>
                            {loadingReactivation === agreement.id ? '...' : <Zap size={12} />}
                        </button>
                    </div>
                );
            }
            return <span className="accelerator-deactivated" title={`Motivo: ${reason}`}><ZapOff size={16} /> Desactivado</span>;
        }
    
        if (agreement.status === 'aprobado' && !isComplete) {
            return (
                <div className="accelerator-status-container">
                    <span>No</span>
                    <button onClick={(e) => { e.stopPropagation(); handleReactivateAccelerator(agreement); }} className="reactivate-button small" disabled={loadingReactivation === agreement.id} title="Activar Acelerador">
                        {loadingReactivation === agreement.id ? '...' : <Zap size={12} />}
                    </button>
                </div>
            );
        }

        return <span>No</span>;
    };

    return (
        <div className="main-panel">
            <div className="mis-acuerdos-header">
                <div>
                    <h2 className="section-title">Mis Acuerdos</h2>
                    <p>Aquí puedes ver el estado y progreso de todas tus participaciones.</p>
                </div>
                <div className="summary-cards-grid">
                    <SummaryCard title="Total de Acuerdos" value={summaryData.total} icon={<Briefcase size={28} />} />
                    <SummaryCard title="Acuerdos Activos" value={summaryData.activos} icon={<Zap size={28} />} />
                    <SummaryCard title="Acuerdos Completados" value={summaryData.completados} icon={<CheckCircle size={28} />} />
                    <SummaryCard title="Aporte activo total" value={`$${summaryData.capitalInvertido.toFixed(2)}`} icon={<Clock size={28} />} />
                </div>
            </div>
            {acceleratorsAboutToExpire.length > 0 && (
                <div className="accelerator-alert-banner">
                    <div className="banner-icon">
                        <AlertTriangle size={24} />
                    </div>
                    <div className="banner-content">
                        <p className="banner-title">Aceleradores por expirar</p>
                        <p className="banner-copy">Te avisamos {ACCELERATOR_WARNING_DAYS === 1 ? '1 día' : `${ACCELERATOR_WARNING_DAYS} días`} antes de que termine el ciclo para que planifiques la reactivación.</p>
                        <div className="accelerator-alert-items">
                            {acceleratorsAboutToExpire.map(alert => (
                                <button
                                    key={alert.agreementId}
                                    className="accelerator-alert-pill"
                                    type="button"
                                    onClick={() => handleOpenDetailsModal(alert.agreement)}
                                >
                                    <span className="pill-id">#{alert.shortId}</span>
                                    <span className="pill-date">expira {alert.endDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span>
                                    <span className="pill-days">({alert.daysLeft} {alert.daysLeft === 1 ? 'día' : 'días'})</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <div className="admin-tabs">
                {TABS.map(tab => (
                    <button key={tab} onClick={() => { setActiveTab(tab); setCurrentPage(1); }} className={activeTab === tab ? 'active' : ''}>{tab}</button>
                ))}
            </div>
            <div className="report-table-container" style={{ marginTop: '1.5rem' }}>
                <table className="report-table">
                    <thead>
                        <tr>
                            <th className="sortable-header" onClick={() => requestSort('id')}>
                                Acuerdo ID {getSortIcon('id')}
                            </th>
                            <th className="sortable-header" onClick={() => requestSort('createdAt')}>
                                Fecha {getSortIcon('createdAt')}
                            </th>
                            <th className="sortable-header" onClick={() => requestSort('netInvestmentAmount')}>
                                Monto aportado {getSortIcon('amount')}
                            </th>
                            <th>Estado</th>
                            <th className="sortable-header" onClick={() => requestSort('progress')}>
                                Progreso de resultados {getSortIcon('progress')}
                            </th>
                            <th className="sortable-header" onClick={() => requestSort('acceleratorStatus')}>
                                Acelerador {getSortIcon('acceleratorStatus')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedAgreements.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center' }}>No hay acuerdos en esta categoría.</td></tr>
                        ) : (
                            paginatedAgreements.map(agreement => {
                                // Usamos los detalles pre-calculados para la clase de resaltado
                                const isNearCompletion = agreement.progressDetails.progress >= 95;

                                return (
                                    <tr
                                        key={agreement.id}
                                        onClick={() => handleOpenDetailsModal(agreement)}
                                        className={isNearCompletion ? 'near-completion-row' : ''}
                                        style={{ cursor: 'pointer' }}>
                                        <td data-label="Acuerdo ID" className="agreement-id-cell">{agreement.id ? agreement.id.substring(0, 6).toUpperCase() : 'N/A'}</td>
                                        <td data-label="Fecha">{agreement.createdAt?.toDate ? agreement.createdAt.toDate().toLocaleDateString('es-ES') : 'N/A'}</td>
                                        <td data-label="Monto aportado">${(agreement.netInvestmentAmount || agreement.amount || 0).toFixed(2)}</td>
                                        <td data-label="Estado"><StatusBadge status={agreement.status} /></td>
                                        <td data-label="Progreso">
                                            <AgreementProgressBarVisual
                                                agreement={agreement} // Pasamos el acuerdo original
                                                userCommissions={commissionAllocation.get(agreement.id) || 0} // Pasamos la comisión calculada
                                                transactions={transactions}
                                                styleType="table"
                                                onComplete={handleAutoCompleteAgreement}
                                            />
                                        </td>
                                        <td data-label="Acelerador">
                                            <AcceleratorInfo agreement={agreement} pendingRequests={pendingRequests} transactions={transactions} />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <Pagination
                    totalItems={sortedAgreements.length}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={setItemsPerPage}
                    currentPage={currentPage}
                    onNext={handleNextPage}
                    onPrevious={handlePreviousPage}
                    canNext={currentPage < totalPages}
                    canPrevious={currentPage > 1}
                />
            )}

            {showDetailsModal && (
                <AgreementDetailsModal
                    agreement={selectedAgreement}
                    onClose={handleCloseDetailsModal}
                    userCommissions={commissionAllocation.get(selectedAgreement.id) || 0}
                    transactions={transactions}
                />
            )}
        </div>
    );
};
export default MisAcuerdos;
